import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { useToast } from "@/shared/ui/Toast";
import { Switch } from "@/shared/ui/Switch";
import { ConfirmModal } from "@/shared/ui/ConfirmModal";
import { Input } from "@/shared/ui/Input";
import { RadioGroup, RadioGroupItem } from "@/shared/ui/RadioGroup";
import { SectionTitle, formatBalance } from "./fields";
import { OpError } from "../api";
import { useSetDeveloper, useAdjustBalance, useGrantPremium } from "../hooks";
import type { CustomerDetail } from "../types";

// ── Sensitive operations (Faza B — 0063.04; redistributed across tabs in 0068) ──
// The three audited, operator-only actions used to live in one "Amallar" section.
// They now sit beside the data they affect: the developer toggle in the Identity
// tab, balance + premium in the Account tab. Each still requires an explicit
// in-place confirm, guards against double-submit (disabled while pending), and
// surfaces backend guard failures (low balance / already premium) inline.

/** Translate an OpError (or any throwable) into the inline message string. */
function opErrorText(e: unknown, t: (k: string) => string): string {
  if (e instanceof OpError) {
    return t(`drawer.${e.code === "generic" ? "opError" : e.code}`);
  }
  return t("drawer.opError");
}

// ── (#4) Developer toggle — backdoor login OTP. Lives in the Identity tab (0068). ──
export function DeveloperToggleBlock({ customer }: { customer: CustomerDetail }) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const setDev = useSetDeveloper();

  const id = customer.id;
  const busy = setDev.isPending;
  const devLabelId = useId();
  const [confirmDev, setConfirmDev] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function runDeveloper() {
    setError(null);
    try {
      await setDev.mutateAsync({ id, body: { is_developer: !customer.is_developer } });
      setConfirmDev(false);
      showToast(t("drawer.opSuccess"));
    } catch (e) {
      setError(opErrorText(e, t));
    }
  }

  return (
    <>
      <SectionTitle className="mt-2">{t("drawer.developerMode")}</SectionTitle>

      <div className="mb-4 rounded-xl border border-grey-200 bg-white px-4 py-3.5">
        <div className="mb-3 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-none text-warning" />
          <p className="text-[12.5px] leading-snug text-grey-600">
            {t("drawer.developerWarning")}
          </p>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span id={devLabelId} className="text-[13px] font-semibold text-grey-800">
            {t("drawer.developerMode")}
          </span>
          {/* Flipping only requests the change — the modal gates the actual mutation.
              While the modal is open the switch shows the pending (target) state. */}
          <Switch
            checked={confirmDev ? !customer.is_developer : customer.is_developer}
            disabled={busy}
            aria-labelledby={devLabelId}
            onChange={() => {
              setError(null);
              setConfirmDev(true);
            }}
          />
        </div>
      </div>

      <ConfirmModal
        open={confirmDev}
        title={customer.is_developer ? t("drawer.removeDeveloper") : t("drawer.makeDeveloper")}
        message={t("drawer.developerWarning")}
        confirmLabel={setDev.isPending ? t("drawer.processing") : t("drawer.confirm")}
        cancelLabel={t("drawer.cancel")}
        busy={busy}
        tone="warning"
        onConfirm={runDeveloper}
        onCancel={() => setConfirmDev(false)}
      />

      {error && <p className="text-[13px] font-medium text-red">{error}</p>}
    </>
  );
}

// ── Account operations — balance adjust (#5b) + premium convert (#7). Account tab (0068). ──
// Balance and premium keep a shared `busy`/`error` so a pending op disables the other.
export function AccountOperations({ customer }: { customer: CustomerDetail }) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const adjust = useAdjustBalance();
  const grant = useGrantPremium();

  const id = customer.id;
  const busy = adjust.isPending || grant.isPending;
  // Stable ids so the amount/reason labels are programmatically tied to their inputs (WCAG 1.3.1).
  const amountId = useId();
  const reasonId = useId();

  // Balance adjust
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [pendingDir, setPendingDir] = useState<"credit" | "debit" | null>(null);
  // Premium grant
  const [premiumFree, setPremiumFree] = useState(false);
  const [confirmPremium, setConfirmPremium] = useState(false);
  // Shared inline error (already translated)
  const [error, setError] = useState<string | null>(null);

  async function runBalance() {
    if (!pendingDir) return;
    setError(null);
    try {
      await adjust.mutateAsync({
        id,
        body: {
          amount,
          direction: pendingDir,
          reason: reason.trim() || undefined,
          // Lightweight double-submit guard the backend honours via ActionLog.
          idempotency_key: crypto.randomUUID(),
        },
      });
      setPendingDir(null);
      setAmount("");
      setReason("");
      showToast(t("drawer.opSuccess"));
    } catch (e) {
      setError(opErrorText(e, t));
    }
  }

  async function runPremium() {
    setError(null);
    try {
      await grant.mutateAsync({ id, body: { is_free: premiumFree } });
      setConfirmPremium(false);
      showToast(t("drawer.opSuccess"));
    } catch (e) {
      setError(opErrorText(e, t));
    }
  }

  const amountNum = Number(amount);
  const amountValid =
    amount.trim() !== "" && Number.isFinite(amountNum) && amountNum > 0;
  const canFree = customer.free_premiums_count > 0;

  return (
    <>
      <SectionTitle className="mt-2">{t("drawer.opsSection")}</SectionTitle>

      {/* (#5b) Balance adjust — amount + reason → confirm credit/debit. */}
      <div className="mb-4 rounded-xl border border-grey-200 bg-white px-4 py-3.5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-[7px]">
            <label htmlFor={amountId} className="text-[12.5px] font-semibold text-grey-700">{t("drawer.amount")}</label>
            <Input
              id={amountId}
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={amount}
              disabled={pendingDir !== null || busy}
              onChange={(e) => {
                setAmount(e.target.value);
                setError(null);
              }}
              className="h-[40px] px-3"
            />
          </div>
          <div className="flex flex-col gap-[7px]">
            <label htmlFor={reasonId} className="text-[12.5px] font-semibold text-grey-700">{t("drawer.reason")}</label>
            <Input
              id={reasonId}
              type="text"
              value={reason}
              placeholder={t("drawer.reasonPlaceholder")}
              disabled={pendingDir !== null || busy}
              onChange={(e) => setReason(e.target.value)}
              className="h-[40px] px-3"
            />
          </div>
        </div>

        {pendingDir ? (
          <div className="mt-3 flex flex-col gap-2">
            <p className="text-[13px] font-medium text-grey-800">
              {t(pendingDir === "credit" ? "drawer.balanceConfirmCredit" : "drawer.balanceConfirmDebit", {
                amount: formatBalance(amount || "0"),
              })}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={runBalance}
                className="flex h-9 items-center rounded-control bg-green px-3.5 text-[13px] font-semibold text-white transition active:scale-95 hover:bg-green-dark disabled:opacity-60 disabled:active:scale-100"
              >
                {adjust.isPending ? t("drawer.processing") : t("drawer.confirm")}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setPendingDir(null)}
                className="flex h-9 items-center rounded-control border border-grey-300 bg-white px-3.5 text-[13px] font-semibold text-grey-700 transition active:scale-95 hover:bg-grey-50 disabled:opacity-60 disabled:active:scale-100"
              >
                {t("drawer.cancel")}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={busy || !amountValid}
              onClick={() => {
                setError(null);
                setPendingDir("credit");
              }}
              className="flex h-9 items-center rounded-control bg-green px-3.5 text-[13px] font-semibold text-white transition active:scale-95 hover:bg-green-dark disabled:opacity-50 disabled:active:scale-100"
            >
              {t("drawer.addFunds")}
            </button>
            <button
              type="button"
              disabled={busy || !amountValid}
              onClick={() => {
                setError(null);
                setPendingDir("debit");
              }}
              className="flex h-9 items-center rounded-control border border-grey-300 bg-white px-3.5 text-[13px] font-semibold text-grey-700 transition active:scale-95 hover:bg-grey-50 disabled:opacity-50 disabled:active:scale-100"
            >
              {t("drawer.withdrawFunds")}
            </button>
          </div>
        )}
      </div>

      {/* (#7) Convert to premium — only when not already premium. */}
      {!customer.is_premium && (
        <div className="mb-4 rounded-xl border border-grey-200 bg-white px-4 py-3.5">
          <label className="text-[12.5px] font-semibold text-grey-700">{t("drawer.convertPremium")}</label>
          <RadioGroup
            className="mt-2"
            value={premiumFree ? "free" : "paid"}
            onValueChange={(v) => {
              setPremiumFree(v === "free");
              setError(null);
            }}
          >
            <label className="flex items-center gap-2 text-[13px] text-grey-700">
              <RadioGroupItem value="paid" disabled={confirmPremium || busy} />
              {t("drawer.premiumPaidOption")}
            </label>
            <label
              className={`flex items-center gap-2 text-[13px] ${canFree ? "text-grey-700" : "text-grey-400"}`}
            >
              <RadioGroupItem value="free" disabled={!canFree || confirmPremium || busy} />
              {t("drawer.premiumFreeOption", { count: customer.free_premiums_count })}
            </label>
          </RadioGroup>

          {confirmPremium ? (
            <div className="mt-3 flex flex-col gap-2">
              <p className="text-[13px] font-medium text-grey-800">
                {t(premiumFree ? "drawer.premiumConfirmFree" : "drawer.premiumConfirmPaid")}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={runPremium}
                  className="flex h-9 items-center rounded-control bg-green px-3.5 text-[13px] font-semibold text-white transition active:scale-95 hover:bg-green-dark disabled:opacity-60 disabled:active:scale-100"
                >
                  {grant.isPending ? t("drawer.processing") : t("drawer.confirm")}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setConfirmPremium(false)}
                  className="flex h-9 items-center rounded-control border border-grey-300 bg-white px-3.5 text-[13px] font-semibold text-grey-700 transition active:scale-95 hover:bg-grey-50 disabled:opacity-60 disabled:active:scale-100"
                >
                  {t("drawer.cancel")}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setError(null);
                setConfirmPremium(true);
              }}
              className="mt-3 flex h-9 items-center rounded-control bg-green px-3.5 text-[13px] font-semibold text-white transition active:scale-95 hover:bg-green-dark disabled:opacity-60 disabled:active:scale-100"
            >
              {t("drawer.convertPremium")}
            </button>
          )}
        </div>
      )}

      {error && <p className="text-[13px] font-medium text-red">{error}</p>}
    </>
  );
}
